import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase/client";
import type { PatientFile } from "../contexts/types";

function toTimestamp(value: unknown) {
  if (!value) return Date.now();

  if (typeof value === "number") return value;

  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? Date.now() : parsed;
  }

  return Date.now();
}

function sanitizeName(value: string) {
  return value
    .trim()
    .replace(/[^\w.\-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function getExtensionFromMimeType(mimeType?: string | null) {
  const normalized = String(mimeType || "").toLowerCase();

  if (normalized.includes("pdf")) return "pdf";
  if (normalized.includes("jpeg")) return "jpg";
  if (normalized.includes("jpg")) return "jpg";
  if (normalized.includes("png")) return "png";
  if (normalized.includes("webp")) return "webp";
  if (normalized.includes("gif")) return "gif";
  if (normalized.includes("heic")) return "heic";
  if (normalized.includes("dcm") || normalized.includes("dicom")) return "dcm";

  return "";
}

function inferType(mimeType?: string | null) {
  const normalized = String(mimeType || "").toLowerCase();

  if (normalized.includes("pdf")) return "application/pdf";
  if (normalized.startsWith("image/")) return normalized;
  if (normalized.includes(".pdf")) return "application/pdf";
  if (normalized.includes(".jpg") || normalized.includes(".jpeg")) return "image/jpeg";
  if (normalized.includes(".png")) return "image/png";
  if (normalized.includes(".webp")) return "image/webp";
  if (normalized.includes(".gif")) return "image/gif";
  if (normalized.includes(".heic")) return "image/heic";
  if (normalized.includes(".dcm") || normalized.includes(".dicom")) return "application/dicom";
  if (normalized.includes("dcm") || normalized.includes("dicom")) return "application/dicom";

  return normalized || "application/octet-stream";
}

function inferPatientFileKind(contentType: string): "image" | "pdf" | "dicom" | "other" {
  const normalized = String(contentType || "").toLowerCase();

  if (normalized.startsWith("image/")) return "image";
  if (normalized.includes("pdf")) return "pdf";
  if (normalized.includes("dcm") || normalized.includes("dicom")) return "dicom";

  return "other";
}

function buildFileLabel(name: string, mimeType?: string | null) {
  const trimmed = name.trim();
  if (trimmed) {
    return trimmed;
  }

  if (String(mimeType || "").toLowerCase().includes("pdf")) {
    return "Exame PDF";
  }

  return "Exame";
}

function buildPatientFileRecord(params: {
  patientId: string;
  fileName: string;
  contentType: string;
  url: string;
  storagePath: string;
  size?: number;
  source: "web";
  uploadedBy?: string;
  name: string;
}): PatientFile {
  const createdAt = new Date().toISOString();
  return {
    id: `${params.patientId}-${params.fileName}-${toTimestamp(createdAt)}`,
    name: params.name,
    fileName: params.fileName,
    url: params.url,
    storagePath: params.storagePath,
    path: params.storagePath,
    contentType: params.contentType,
    mimeType: params.contentType,
    kind: inferPatientFileKind(params.contentType),
    type: params.contentType,
    source: params.source,
    uploadedBy: params.uploadedBy || params.source,
    size: params.size,
    createdAt,
  };
}
export async function uploadPatientDataUrl(params: {
    
  uid: string;
  patientId: string;
  dataUrl: string; // data:image/png;base64,...
}) {
  const { uid, patientId, dataUrl } = params;

  if (!uid) throw new Error("Missing UID for upload");
  if (!patientId) throw new Error("Missing patientId for upload");
  if (!dataUrl?.startsWith("data:")) throw new Error("Invalid dataUrl for upload");

  const blob = dataUrlToBlob(dataUrl);
  const normalizedType = inferType(blob.type);
  const ext = getExtensionFromMimeType(normalizedType) || "bin";

  const filePath = `users/${uid}/patients/${patientId}/files/${Date.now()}.${ext}`;
  const fileRef = ref(storage, filePath);

  console.log("[PatientFiles][add:start]", { patientId, uid, filePath, ext, contentType: normalizedType, size: blob.size });

  await uploadBytes(fileRef, blob, { contentType: normalizedType });
  const url = await getDownloadURL(fileRef);
  console.log("[PatientFiles][upload:success]", { patientId, filePath, url });

  return buildPatientFileRecord({
    patientId,
    fileName: `${Date.now()}.${ext}`,
    contentType: normalizedType,
    url,
    storagePath: filePath,
    size: blob.size,
    source: "web",
    uploadedBy: uid,
    name: buildFileLabel("Exame", blob.type),
  });
}
/**
 * Converte dataURL (base64) em Blob real
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  if (!dataUrl || !dataUrl.includes(",")) {
    throw new Error("Invalid dataUrl provided to dataUrlToBlob");
  }

  const [header, base64] = dataUrl.split(",");
  const mimeMatch = header.match(/data:(.*?);base64/);
  const mime = mimeMatch?.[1] ?? "application/octet-stream";

  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new Blob([bytes], { type: mime });
}

/**
 * Upload de imagem gerada pelo Atlas (base64)
 */
export async function uploadAtlasImage(params: {
  uid: string;
  patientId: string;
  dataUrl: string;
  kind?: "static" | "postop" | "xray-ap" | "xray-lat" | "xray-obl";
}) {
  const { uid, patientId, dataUrl, kind = "static" } = params;

  if (!uid) throw new Error("Missing UID for upload");
  if (!patientId) throw new Error("Missing patientId for upload");
  if (!dataUrl?.startsWith("data:")) {
    throw new Error("Invalid image data for upload");
  }

  try {
    console.log("📤 Starting Atlas upload...");
    console.log("UID:", uid);
    console.log("Patient:", patientId);

    const blob = dataUrlToBlob(dataUrl);

    const ext = getExtensionFromMimeType(blob.type) || "bin";

    const filePath = `users/${uid}/patients/${patientId}/atlas/${kind}-${Date.now()}.${ext}`;
    const fileRef = ref(storage, filePath);

    await uploadBytes(fileRef, blob, {
      contentType: blob.type,
    });

    const url = await getDownloadURL(fileRef);

    console.log("✅ Atlas upload success:", filePath);

    return {
      url,
      path: filePath,
      contentType: blob.type,
      size: blob.size,
      kind,
      createdAt: Date.now(),
    };
  } catch (error: any) {
    console.error("❌ Atlas upload failed:", error);
    throw error;
  }
}

/**
 * Upload direto de arquivo real do paciente (File)
 * Para raio-x real, fotos, PDFs etc.
 */
export async function uploadPatientFile(params: {
  uid: string;
  patientId: string;
  file: File;
}) {
  const { uid, patientId, file } = params;

  if (!uid) throw new Error("Missing UID for upload");
  if (!patientId) throw new Error("Missing patientId for upload");
  if (!file) throw new Error("Missing file for upload");

  try {
    const safeName = sanitizeName(file.name || "exame") || "exame";
    const normalizedType = inferType(file.type || file.name);
    const ext = getExtensionFromMimeType(normalizedType) || String(file.name || "").split(".").pop() || "bin";
    const filePath = `users/${uid}/patients/${patientId}/files/${Date.now()}-${safeName}.${ext}`;
    const fileRef = ref(storage, filePath);

    console.log("[PatientFiles][add:start]", {
      patientId,
      uid,
      fileName: file.name,
      size: file.size,
      mimeType: normalizedType,
      filePath,
    });

    await uploadBytes(fileRef, file, {
      contentType: normalizedType,
    });

    const url = await getDownloadURL(fileRef);

    console.log("[PatientFiles][upload:success]", { patientId, uid, filePath, url });

    return buildPatientFileRecord({
      patientId,
      fileName: `${Date.now()}-${safeName}.${ext}`,
      contentType: normalizedType,
      url,
      storagePath: filePath,
      size: file.size,
      source: "web",
      uploadedBy: uid,
      name: buildFileLabel(file.name, file.type),
    });
  } catch (error: any) {
    console.error("[PatientFiles][error]", error);
    throw error;
  }
}
